

























import { defineComponent, computed, Ref, ref, watch } from '@vue/composition-api';
import { LabelInput } from '@nimiq/vue-components';
import { useBtcLabelsStore } from '@/stores/BtcLabels';
import Avatar from './Avatar.vue';

const BtcLabelInput = defineComponent({
    props: {
        value: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        // Nested v-model pattern
        // https://zaengle.com/blog/using-v-model-on-nested-vue-components
        // https://vue-composition-api-rfc.netlify.app/api.html#computed
        const localValue = computed({
            get: () => props.value,
            set(value) { context.emit('input', value); },
        });

        const { recipientLabels } = useBtcLabelsStore();
        const input$ = ref<LabelInput>(null);
        const btcLabelAutocomplete$ = ref<HTMLUListElement>(null);
        const selectedLabelIndex: Ref<number> = ref(0);

        function focus() {
            if (!input$.value) return;
            input$.value.focus();
        }

        /* Filtering Labels */
        const matchingLabels = computed(() =>
            localValue.value && Object.values(recipientLabels.value).filter((label) =>
                label
                && label.toLowerCase().startsWith(localValue.value.toLowerCase())
                && label.toLowerCase() !== localValue.value.toLowerCase(),
            ),
        );

        /* Reset the currently selected label to 0 on text input */
        watch(localValue, () => selectedLabelIndex.value = 0);

        /* Keyboard navigation */
        function onKeyDown(event: KeyboardEvent) {
            if (!matchingLabels || !btcLabelAutocomplete$.value) return;
            const oldIndex = selectedLabelIndex.value;

            switch (event.keyCode) {
                case 40: // down arrow key
                    selectedLabelIndex.value = Math.max(0, Math.min(
                        matchingLabels.value.length - 1, selectedLabelIndex.value + 1),
                    );

                    break;

                case 38: // up arrow key
                    selectedLabelIndex.value = Math.max(0, Math.min(
                        matchingLabels.value.length - 1, selectedLabelIndex.value - 1),
                    );
                    break;

                case 13: // enter key
                    localValue.value = matchingLabels.value[selectedLabelIndex.value];
                    break;

                default:
                    break;
            }

            if (selectedLabelIndex.value !== oldIndex) {
                /* If the selected label list item is not into view, scroll to it */
                btcLabelAutocomplete$.value.children[selectedLabelIndex.value].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }

        return {
            focus, // exposed for use from other components

            localValue,
            input$,
            btcLabelAutocomplete$,
            recipientLabels,
            matchingLabels,
            selectedLabelIndex,
            onKeyDown,
        };
    },
    components: {
        LabelInput,
        Avatar,
    },
});
// Export the component's instance type alongside the value (the constructor) via Typescript declaration merging,
// similar to what would be the case for a class-based component declaration, for convenient usage in Ref types.
type BtcLabelInput = InstanceType<typeof BtcLabelInput>;
export default BtcLabelInput;
